import clsx from "clsx";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { formatDate, monthBounds, shiftMonth } from "@/lib/format";
import { Input } from "./Field";

export interface Range {
  from: string;
  to: string;
}

interface Preset {
  key: string;
  label: string;
  range: (today: string) => Range;
}

function daysBefore(today: string, n: number): string {
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const PRESETS: Preset[] = [
  { key: "mtd", label: "This month", range: (t) => ({ from: monthBounds(t.slice(0, 7)).from, to: t }) },
  {
    key: "last-month",
    label: "Last month",
    range: (t) => monthBounds(shiftMonth(t.slice(0, 7), -1)),
  },
  { key: "30d", label: "Last 30 days", range: (t) => ({ from: daysBefore(t, 29), to: t }) },
  { key: "90d", label: "Last 90 days", range: (t) => ({ from: daysBefore(t, 89), to: t }) },
  {
    key: "12m",
    label: "Last 12 months",
    range: (t) => ({ from: `${shiftMonth(t.slice(0, 7), -11)}-01`, to: t }),
  },
  { key: "ytd", label: "Year to date", range: (t) => ({ from: `${t.slice(0, 4)}-01-01`, to: t }) },
];

/** Presets as rows (nobody fights a calendar for "last 30 days"); custom range in the footer. */
export function DateRange({ value, onChange, today, allowEmpty = false }: {
  value: Range | null;
  onChange: (range: Range | null) => void;
  today: string;
  allowEmpty?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Range>(value ?? { from: "", to: "" });
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => setDraft(value ?? { from: "", to: "" }), [value]);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = value
    ? PRESETS.find((p) => {
        const r = p.range(today);
        return r.from === value.from && r.to === value.to;
      })
    : undefined;
  const label = !value
    ? "All dates"
    : active
      ? active.label
      : `${formatDate(value.from, true)} – ${formatDate(value.to, true)}`;

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-sm text-ink hover:bg-surface-2"
      >
        <span className="text-ink-3">Dates</span>
        <span className="font-medium">{label}</span>
        <ChevronDown className="size-4 text-ink-3" aria-hidden />
      </button>
      {open && (
        <div
          id={listId}
          className="absolute left-0 z-30 mt-1 w-72 rounded-xl border border-border bg-surface p-1 shadow-lg"
        >
          <ul className="py-1">
            {allowEmpty && (
              <PresetRow label="All dates" selected={!value} onClick={() => { onChange(null); setOpen(false); }} />
            )}
            {PRESETS.map((p) => (
              <PresetRow
                key={p.key}
                label={p.label}
                selected={active?.key === p.key}
                onClick={() => {
                  onChange(p.range(today));
                  setOpen(false);
                }}
              />
            ))}
          </ul>
          <form
            className="flex flex-col gap-2 border-t border-line p-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.from && draft.to && draft.from <= draft.to) {
                onChange(draft);
                setOpen(false);
              }
            }}
          >
            <p className="text-xs font-medium text-ink-3">Custom range</p>
            <div className="flex items-center gap-2">
              <Input type="date" aria-label="From" value={draft.from} max={draft.to || undefined}
                     onChange={(e) => setDraft({ ...draft, from: e.target.value })} className="h-9 text-sm" />
              <Input type="date" aria-label="To" value={draft.to} min={draft.from || undefined}
                     onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="h-9 text-sm" />
            </div>
            <button
              type="submit"
              disabled={!draft.from || !draft.to || draft.from > draft.to}
              className="h-8 rounded-lg bg-accent text-sm font-medium text-on-accent disabled:opacity-40"
            >
              Apply
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function PresetRow({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={clsx(
          "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-2",
          selected ? "font-semibold text-ink" : "text-ink-2",
        )}
      >
        {label}
        {selected && <Check className="size-4" strokeWidth={3} aria-hidden />}
      </button>
    </li>
  );
}
